import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { Branch } from '../models/brand.model';
import { FulfillmentMode } from '../models/order.model';
import { Customer } from '../models/auth.model';
import { PREVIEW } from '../preview/preview-mode';

export interface Profile { email: string; birthday: string; gender: string; }
export interface ActiveBranch { id: string; name: string; area: string; }

function load<T>(key: string, fallback: T): T {
  try { const v = localStorage.getItem(key); return v ? (JSON.parse(v) as T) : fallback; } catch { return fallback; }
}
/**
 * The admin live preview always shows a guest: the iframe shares storage with the real storefront
 * on this origin, so a signed-in customer (or admin) there must not leak into the preview.
 */
const GUEST_ONLY = PREVIEW.active;

function save(key: string, v: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* ignore */ }
}

/** User session: auth, fulfillment mode, chosen branch, active order. */
@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly _loggedIn = new BehaviorSubject<boolean>(GUEST_ONLY ? false : load('ff_loggedIn', false));
  private readonly _userName = new BehaviorSubject<string>(GUEST_ONLY ? '' : load('ff_name', ''));
  private readonly _fulfillment = new BehaviorSubject<FulfillmentMode>(load('ff_fulfillment', 'delivery'));
  // Neutral until the customer picks an order type / branch (real branches come from BranchService).
  private readonly _branch = new BehaviorSubject<ActiveBranch>(
    load('ff_branch', { id: '', name: '', area: 'Select location' }),
  );

  profile: Profile = load('ff_profile', { email: '', birthday: '', gender: '' });
  activeOrder = false;
  readonly orderNo = 1183 + Math.floor(Math.random() * 40);

  /** Whether the user has completed order-type + branch/address setup. */
  private readonly _configured = new BehaviorSubject<boolean>(load('ff_configured', false));
  readonly configured$ = this._configured.asObservable();
  /** id of the selected delivery address (delivery mode), if any. */
  addressId: string | null = load('ff_address_id', null);
  /**
   * Last known device location — sent as X-Latitude / X-Longitude on every API call (nearest
   * branch + distances). Kept across refreshes so the very first /branches call at app start
   * already carries it.
   */
  coords: { lat: number; lng: number } | null = load('ff_coords', null);
  setCoords(lat: number, lng: number): void { this.coords = { lat, lng }; save('ff_coords', this.coords); }
  /** Whether the dine-in table QR has been scanned for the current setup. */
  private _tableScanned = load('ff_table_scanned', false);

  /** Bearer JWT + authenticated customer (from the auth API). */
  private _token: string | null = GUEST_ONLY ? null : load('ff_token', null);
  customer: Customer | null = GUEST_ONLY ? null : load('ff_customer', null);

  readonly loggedIn$ = this._loggedIn.asObservable();
  readonly userName$ = this._userName.asObservable();
  readonly fulfillment$ = this._fulfillment.asObservable();
  readonly branch$ = this._branch.asObservable();

  get loggedIn(): boolean { return this._loggedIn.value; }
  get userName(): string { return this._userName.value; }
  get token(): string | null { return this._token; }
  get configured(): boolean { return this._configured.value; }
  get fulfillment(): FulfillmentMode { return this._fulfillment.value; }
  get branch(): ActiveBranch { return this._branch.value; }

  markConfigured(): void {
    if (!this._configured.value) { this._configured.next(true); save('ff_configured', true); }
  }
  setAddressId(id: string | null): void {
    this.addressId = id;
    save('ff_address_id', id);
  }

  /** Dine-in: whether the table QR has been scanned for the current selection. */
  get dineTableScanned(): boolean { return this._tableScanned; }
  setTableScanned(v: boolean): void { this._tableScanned = v; save('ff_table_scanned', v); }

  signIn(name?: string): void {
    if (name) this.setName(name);
    this._loggedIn.next(true);
    save('ff_loggedIn', true);
  }

  /** Persist a successful auth result (token + customer) and mark signed in. */
  setAuth(token: string, customer: Customer): void {
    this._token = token;
    this.customer = customer;
    save('ff_token', token);
    save('ff_customer', customer);
    if (customer.email) this.profile = { ...this.profile, email: customer.email };
    this.setName(customer.name || '');
    this._loggedIn.next(true);
    save('ff_loggedIn', true);
  }

  /** Refresh the cached customer (e.g. from /auth/me). */
  setCustomer(customer: Customer): void {
    this.customer = customer;
    save('ff_customer', customer);
    if (customer.name) this.setName(customer.name);
    this._loggedIn.next(true);
    save('ff_loggedIn', true);
  }

  signOut(): void {
    this._loggedIn.next(false);
    this._token = null;
    this.customer = null;
    save('ff_loggedIn', false);
    try { localStorage.removeItem('ff_token'); localStorage.removeItem('ff_customer'); } catch { /* ignore */ }
    // Account-scoped data must not linger in the view after logout: clear the
    // delivery address/branch selection and the customer's name/profile.
    this.clearAccountData();
  }

  /** Reset the delivery selection + personal data to the neutral guest state. */
  private clearAccountData(): void {
    this.addressId = null;
    this._configured.next(false);
    this._branch.next({ id: '', name: '', area: 'Select location' });
    this._tableScanned = false;
    this._userName.next('');
    this.profile = { email: '', birthday: '', gender: '' };
    try {
      ['ff_address_id', 'ff_configured', 'ff_branch', 'ff_table_scanned', 'ff_name', 'ff_profile']
        .forEach((k) => localStorage.removeItem(k));
    } catch { /* ignore */ }
  }
  setName(name: string): void {
    this._userName.next(name);
    save('ff_name', name);
  }
  updateProfile(name: string, p: Profile): void {
    this.setName(name);
    this.profile = p;
    save('ff_profile', p);
  }

  setFulfillment(mode: FulfillmentMode): void {
    this._fulfillment.next(mode);
    save('ff_fulfillment', mode);
  }
  setBranch(b: Branch | ActiveBranch): void {
    const val: ActiveBranch = { id: b.id, name: b.name, area: b.area };
    this._branch.next(val);
    save('ff_branch', val);
  }
}
